import React, {useState} from 'react'
import PropTypes from 'prop-types'

// Components
import {
    Box,
    Button,
    ButtonGroup,
    Flex,
    HStack,
    VStack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import CartItemVariantName from '@app/components/item-variant/item-name'
import CartItemVariantPrice from '@app/components/item-variant/item-price'
import ConfirmationModal from '@app/components/confirmation-modal'
import DynamicImage from '@app/components/dynamic-image'
import ItemVariantProvider from '@app/components/item-variant'
import ProductViewModal from '@app/components/product-view-modal'

// Hooks
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useCurrentBasket} from '@app/hooks/use-current-basket'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useDisclosure, useStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import {
    useShopperCustomersMutation,
    useShopperBasketsMutationHelper
} from '@salesforce/commerce-sdk-react'
import {useToast} from '@app/hooks/use-toast'
import {useWishList} from '@app/hooks/use-wish-list'
import useEinstein from '@app/hooks/use-einstein'
import {useGTM} from '@app/hooks/use-gtm'

// Utils
import {defineMessages} from 'react-intl'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'

// Constants
import {API_ERROR_MESSAGE} from '@app/constants'

const CONFIRM_DIALOG_MESSAGES = defineMessages({
    dialogTitle: {
        defaultMessage: 'Confirm Remove Item',
        id: 'confirmation_modal.remove_wishlist_item.title.confirm_remove'
    },
    confirmationMessage: {
        defaultMessage: 'Are you sure you want to remove this item from your wishlist?',
        id: 'confirmation_modal.remove_wishlist_item.message.sure_to_remove'
    },
    primaryActionLabel: {
        defaultMessage: 'Yes, remove item',
        id: 'confirmation_modal.remove_wishlist_item.action.yes'
    },
    primaryActionAriaLabel: {
        defaultMessage: 'Yes, remove item',
        id: 'confirmation_modal.remove_cart_item.assistive_msg.yes'
    },
    alternateActionLabel: {
        defaultMessage: 'No, keep item',
        id: 'confirmation_modal.remove_wishlist_item.action.no'
    },
    alternateActionAriaLabel: {
        defaultMessage: 'No, keep item',
        id: 'confirmation_modal.remove_cart_item.assistive_msg.no'
    }
})

/**
 * Represents a card component for displaying an item on the wishlist page. This component
 * provides functionalities to view product options (ProductViewModal), remove an item from the wishlist..
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.wishlistItem - Wish list item data and product details.
 * @param {Object} props.wishlistItem.wishlistItemData - Wish list item data.
 * @param {Object} props.wishlistItem.productDetails - Product details.
 *
 * @component
 */
export const WishlistItemCard = ({wishlistItem}) => {
    const {wishlistItemData, productDetails} = wishlistItem

    const confirmRemoveModal = useDisclosure()
    const einstein = useEinstein()
    const intl = useIntl()
    const productViewModal = useDisclosure()
    const styles = useStyleConfig('Cart')
    const toast = useToast()
    const {triggerAddToCartEvent} = useGTM()

    const {data: customer} = useCurrentCustomer()
    const {data: wishList} = useWishList()
    const {refetch: refetchBasket} = useCurrentBasket()
    const {currency: activeCurrency} = useCurrency()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const deleteCustomerProductListItem = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )

    const [basketActionInProgress, setBasketActionInProgress] = useState(false)

    const showGenericApiError = () => {
        toast({title: intl.formatMessage(API_ERROR_MESSAGE), description: '', status: 'error'})
    }

    /**
     * Handles the removal of an item from the customer's wishlist.
     *
     * @async
     * @function handleItemRemove
     * @returns {Promise<void>} A promise that resolves when the item removal process is complete.
     * @throws Will show a generic API error if the item removal fails.
     */
    const handleItemRemove = async () => {
        try {
            await deleteCustomerProductListItem.mutateAsync({
                parameters: {
                    customerId: customer.customerId,
                    listId: wishList?.id,
                    itemId: wishlistItemData.id
                }
            })
            toast({
                title: intl.formatMessage({
                    defaultMessage: 'Item removed from wishlist',
                    id: 'wishlist_secondary_button_group.info.item_removed'
                }),
                status: 'success',
                description: ''
            })
        } catch {
            showGenericApiError()
        }
    }

    /**
     * Handles the addition of a product variant to the shopping cart.
     *
     * @async
     * @param {Object} variant - The product variant to be added to the cart.
     * @param {string} variant.productId - The unique identifier of the product variant.
     * @param {number} quantity - The quantity of the product variant to add to the cart.
     * @returns {Promise<void>} - A promise that resolves when the operation is complete.
     * @throws Will show a generic API error if the addition fails.
     */
    const handleAddProductToCart = async (variant, quantity) => {
        try {
            setBasketActionInProgress(true)
            const productItems = [{productId: variant.productId, quantity}]
            const basketResponse = await addItemToNewOrExistingBasket(productItems)
            await refetchBasket()
            einstein.sendAddToCart(productItems)
            productViewModal.onClose()

            // GTM Integration
            triggerAddToCartEvent(basketResponse?.productItems, variant)

            // Return data so the `AddToCartModal` is displayed
            return [{product: productDetails, variant, quantity}]
        } catch {
            showGenericApiError()
        } finally {
            setBasketActionInProgress(false)
        }
    }

    // We find the 'front' images in the variant's image groups based on variationValues and pick the first one
    const image = findImageGroupBy(productDetails?.imageGroups, {
        viewType: 'front',
        selectedVariationAttributes: productDetails?.variationValues
    })?.images?.[0]

    return (
        <ItemVariantProvider variant={productDetails}>
            <Box
                key={productDetails.id}
                {...styles.cardContainer}
                role="group"
                aria-label={intl.formatMessage({
                    defaultMessage: 'Product',
                    id: 'add_to_cart_modal.aria_label.product'
                })}
            >
                <HStack gap={{base: 16, lg: 24}} alignItems="flex-start">
                    <DynamicImage
                        {...styles.cardImage}
                        flexShrink={0}
                        src={`${image?.disBaseLink || image?.link}[?sw={width}]`}
                        widths={Object.values(styles.cardImage.width)}
                        position="relative"
                        height="100%"
                        imageProps={{
                            width: '100%',
                            height: '100%',
                            loading: 'auto',
                            fetchpriority: 'auto',
                            objectFit: 'cover',
                            alt: ''
                        }}
                    />

                    <VStack flexGrow={1} alignItems="stretch" gap={32}>
                        <Flex
                            flexGrow={1}
                            direction={{base: 'column', md: 'row'}}
                            justifyContent={{md: 'space-between'}}
                            gap={16}
                        >
                            <CartItemVariantName {...styles.cardHeading} />
                            <CartItemVariantPrice align="flex-end" currency={activeCurrency} />
                        </Flex>

                        <ButtonGroup
                            flexDirection={{base: 'column', md: 'row'}}
                            gap={32}
                            alignItems={{base: 'flex-start', md: 'center'}}
                        >
                            <Button variant="primary" onClick={productViewModal.onOpen}>
                                <FormattedMessage
                                    defaultMessage="View Options"
                                    id="wishlist_primary_action.button.view_options"
                                />
                            </Button>
                            <Button variant="link" onClick={confirmRemoveModal.onOpen}>
                                <FormattedMessage
                                    defaultMessage="Remove"
                                    id="wishlist_secondary_button_group.action.remove"
                                />
                            </Button>
                        </ButtonGroup>
                    </VStack>
                </HStack>

                <ProductViewModal
                    {...productViewModal}
                    product={productDetails}
                    showImageGalleryThumbnails
                    addToCart={handleAddProductToCart}
                    isBasketLoading={basketActionInProgress}
                    useUpdateLayout={true}
                    shouldHideBreadcrumb={true}
                />

                <ConfirmationModal
                    dialogTitle={CONFIRM_DIALOG_MESSAGES.dialogTitle}
                    confirmationMessage={CONFIRM_DIALOG_MESSAGES.confirmationMessage}
                    primaryActionLabel={CONFIRM_DIALOG_MESSAGES.primaryActionLabel}
                    primaryActionAriaLabel={CONFIRM_DIALOG_MESSAGES.primaryActionAriaLabel}
                    alternateActionLabel={CONFIRM_DIALOG_MESSAGES.alternateActionLabel}
                    alternateActionAriaLabel={CONFIRM_DIALOG_MESSAGES.alternateActionAriaLabel}
                    onPrimaryAction={handleItemRemove}
                    {...confirmRemoveModal}
                />
            </Box>
        </ItemVariantProvider>
    )
}

WishlistItemCard.propTypes = {
    wishlistItem: PropTypes.object
}
